import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Terminal, ShieldCheck, Users, LineChart } from "lucide-react";
import StatusTicker from "@/components/StatusTicker";
import BrandMark from "@/components/BrandMark";
import SignupDialog from "@/components/SignupDialog";
import SiteFooter from "@/components/SiteFooter";

export default function ComingSoon() {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [signupOpen, setSignupOpen] = useState(false);

  const dummyLogin = () => {
    // One-click demo access — seed a lightweight guest profile if the
    // visitor hasn't signed up. Existing members keep their profile.
    try {
      const existing = JSON.parse(localStorage.getItem("deployd_member") || "null");
      if (!existing || !existing.email) {
        localStorage.setItem(
          "deployd_member",
          JSON.stringify({
            email: "guest@deployd.app",
            industry: "saas",
            full_name: "Guest CFO",
            company: "Deployd Guest",
          })
        );
      }
    } catch {
      localStorage.setItem(
        "deployd_member",
        JSON.stringify({
          email: "guest@deployd.app",
          industry: "saas",
          full_name: "Guest CFO",
          company: "Deployd Guest",
        })
      );
    }
    navigate("/dashboard");
  };

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeStr = now.toLocaleTimeString("en-US", {
    hour12: false,
    timeZone: "America/Los_Angeles",
  });

  return (
    <main
      data-testid="coming-soon-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F7F6F1] text-zinc-900"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 10%, rgba(22,163,74,0.10), transparent 42%), radial-gradient(circle at 85% 92%, rgba(22,163,74,0.06), transparent 45%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.4]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.06) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)",
        }}
      />
      <div className="grain-overlay" />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between border-b border-zinc-200 px-6 py-4 sm:px-10 lg:px-16">
        <div className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          <Terminal className="h-3.5 w-3.5 text-emerald-600" />
          <span data-testid="brand-mark">
            <BrandMark size="inline" /> / system
          </span>
        </div>
        <div className="hidden items-center gap-6 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500 sm:flex">
          <span data-testid="location-tag">SF · CA</span>
          <span className="inline-flex items-center gap-2" data-testid="clock">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00D752] shadow-[0_0_8px_rgba(0,215,82,0.7)]" />
            {timeStr} PT
          </span>
          <button
            type="button"
            data-testid="signin-btn"
            onClick={dummyLogin}
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 py-1.5 text-[11px] font-medium normal-case tracking-normal text-zinc-700 transition-colors hover:border-emerald-500 hover:text-emerald-700"
          >
            Sign in
          </button>
        </div>
        <button
          type="button"
          data-testid="signin-btn-mobile"
          onClick={dummyLogin}
          className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 py-1.5 text-[11px] font-medium text-zinc-700 sm:hidden"
        >
          Sign in
        </button>
      </header>

      {/* HERO — centered single column */}
      <section className="relative z-10 mx-auto flex w-full max-w-4xl flex-col items-center px-6 pb-16 pt-12 text-center sm:px-10 sm:pt-16 lg:pt-20">
        <div className="reveal flex w-full min-w-0 flex-col items-center">
          <h2 className="mb-6 font-display text-[clamp(3.25rem,9vw,7.5rem)] font-extrabold leading-[0.9] tracking-tight text-zinc-900">
            <BrandMark size="hero" pulsing />
          </h2>

          <p className="mb-5 font-mono-tech text-xs uppercase tracking-[0.4em] text-zinc-500">
            Launching soon &middot; Built for the CIO&apos;s office<span className="caret">_</span>
          </p>

          <h1
            data-testid="hero-title"
            className="max-w-3xl font-display text-[clamp(1.75rem,4vw,3.25rem)] font-extrabold leading-[1.08] tracking-tight text-zinc-900"
          >
            See which <span className="text-emerald-600">AI tools</span> are{" "}
            <em className="not-italic font-extrabold text-zinc-900 underline decoration-emerald-500 decoration-2 underline-offset-[6px]">
              actually
            </em>{" "}
            being used by{" "}
            <span className="text-zinc-500">companies like yours.</span>
          </h1>

          <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-700 sm:text-lg">
            Search verified AI deployments by industry and function &mdash;{" "}
            <span className="font-semibold text-zinc-900">who is using what</span>, for{" "}
            <span className="font-semibold text-zinc-900">which use case</span>, and with{" "}
            <span className="font-semibold text-zinc-900">what results.</span>
          </p>

          <p className="mt-4 max-w-2xl font-display text-lg font-semibold leading-snug text-zinc-900 sm:text-xl">
            Real companies. Real deployments.{" "}
            <span className="text-emerald-600">Source-backed evidence.</span>
          </p>

          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row">
            <button
              type="button"
              onClick={() => setSignupOpen(true)}
              data-testid="count-me-in-btn"
              className="group inline-flex items-center gap-3 border border-zinc-900 bg-zinc-900 px-7 py-4 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600"
            >
              <span>Join the waitlist</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
              Official email &middot; ~ 30 sec &middot; no spam
            </p>
          </div>

          <p className="mt-10 max-w-2xl border-t-2 border-emerald-500 bg-white/60 px-6 py-4 font-display text-lg font-semibold leading-snug text-zinc-900 backdrop-blur sm:text-xl">
            Researching an AI tool?{" "}
            <span className="text-emerald-600">Don&apos;t start with the vendor.</span>{" "}
            Start with who has already deployed it.
          </p>
        </div>

      </section>

      {/* WHY DEPLOYD strip */}
      <section
        data-testid="why-deployd"
        className="relative z-10 border-y border-zinc-200 bg-white/50 py-16 sm:py-20"
      >
        <div className="mx-auto w-full max-w-7xl px-6 sm:px-10 lg:px-16">
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.32em] text-emerald-600">
            // why <BrandMark size="inline" />
          </p>
          <h2 className="mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight tracking-tight text-zinc-900 sm:text-4xl">
            Because <span className="text-emerald-600">verified</span> beats vendor claims.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-3">
            <Pillar
              icon={<ShieldCheck className="h-5 w-5" />}
              title="Customer verified"
              body="Every metric is reviewed with the customer's team before it's published. No unnamed logos, no anonymised case studies."
            />
            <Pillar
              icon={<Users className="h-5 w-5" />}
              title="Peer-led signal"
              body="Curated from your industry. See exactly what your peers are running, at what scale, and what actually delivered."
            />
            <Pillar
              icon={<LineChart className="h-5 w-5" />}
              title="Independent"
              body="No vendor sponsorships, no analyst paywall. Just the on-the-ground reality of what's shipping."
            />
          </div>
        </div>
      </section>

      <StatusTicker />

      <SiteFooter />

      <SignupDialog open={signupOpen} onOpenChange={setSignupOpen} />
    </main>
  );
}

const Pillar = ({ icon, title, body }) => (
  <div className="bg-white p-7 sm:p-8">
    <span className="inline-flex items-center justify-center border border-emerald-500/40 bg-emerald-50 p-2 text-emerald-700">
      {icon}
    </span>
    <h3 className="mt-4 font-display text-xl font-extrabold tracking-tight text-zinc-900 sm:text-2xl">
      {title}
    </h3>
    <p className="mt-3 text-sm leading-relaxed text-zinc-700 sm:text-base">{body}</p>
  </div>
);
